import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase/client';
import { Loader2, AlertCircle, Mail, KeyRound } from 'lucide-react';
import logoEmblem from '@/assets/logos/vehix-logo-emblem.png';

export const InviteConfirmPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow digits, strip everything else
    const value = e.target.value.replace(/[^0-9]/g, '');
    setOtpCode(value);
  };

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    // Extract only digits and limit to 6 characters
    const digits = pasted.replace(/[^0-9]/g, '').slice(0, 6);
    setOtpCode(digits);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Basic validation
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (otpCode.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setIsSubmitting(true);

    try {
      console.log('[InviteConfirmPage] Verifying OTP for email:', email.trim().toLowerCase());

      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        email: email.trim().toLowerCase(),
        token: otpCode,
        type: 'invite'
      });

      if (verifyError) {
        console.error('[InviteConfirmPage] OTP verification error:', verifyError);

        // Handle specific error cases
        if (
          verifyError.message.includes('expired') ||
          verifyError.message.includes('Token has expired')
        ) {
          setError('This code has expired. Please contact your administrator for a new invite.');
        } else if (
          verifyError.message.includes('invalid') ||
          verifyError.message.includes('Invalid')
        ) {
          setError('Invalid code. Please check and try again.');
        } else {
          setError(verifyError.message || 'Verification failed. Please try again.');
        }
        return;
      }

      if (data?.session) {
        console.log('[InviteConfirmPage] OTP verified successfully, redirecting to password setup');
        // Success - redirect to password setup page
        navigate('/account/set-password', { replace: true });
      } else {
        setError('Verification failed. Please try again.');
      }
    } catch (err) {
      console.error('[InviteConfirmPage] Unexpected error:', err);
      setError('Unable to verify code. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className='min-h-screen bg-[#f3f0e8] flex items-center justify-center p-4'>
      <div className='w-full max-w-md bg-white rounded-lg shadow-lg p-8'>
        <div className='text-center mb-6'>
          <div className='flex justify-center mb-4'>
            <img src={logoEmblem} alt='Vehix Logo' className='w-32 h-32' />
          </div>
          <div className='flex items-center justify-center mb-2'>
            <KeyRound className='h-6 w-6 text-[#d4854a] mr-2' />
            <h1 className='text-2xl font-bold text-gray-900'>Confirm Your Invite</h1>
          </div>
          <p className='text-gray-600'>
            Enter your email and the 6-digit code from your invitation email.
          </p>
        </div>

        {error && (
          <div
            className='mb-4 p-3 bg-red-50 border border-red-200 rounded-md
            flex items-center gap-2'
          >
            <AlertCircle className='h-5 w-5 text-red-600 flex-shrink-0' />
            <p className='text-sm text-red-600'>{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className='space-y-4'>
          <div>
            <label htmlFor='email' className='block text-sm font-medium text-gray-900 mb-2'>
              Email Address
            </label>
            <div className='relative'>
              <input
                id='email'
                type='email'
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder='Enter your email'
                className={[
                  'w-full px-3 py-2 pl-10 border border-gray-300 bg-white',
                  'text-gray-900 rounded-md focus:outline-none focus:ring-2',
                  'focus:ring-[#d4854a] focus:border-transparent placeholder:text-gray-400'
                ].join(' ')}
                required
                disabled={isSubmitting}
                autoComplete='email'
              />
              <Mail className='absolute left-3 top-2.5 h-5 w-5 text-gray-400' />
            </div>
          </div>

          <div>
            <label htmlFor='otp-code' className='block text-sm font-medium text-gray-900 mb-2'>
              One-Time Code
            </label>
            <input
              id='otp-code'
              type='text'
              inputMode='numeric'
              pattern='[0-9]*'
              maxLength={6}
              value={otpCode}
              onChange={handleOtpChange}
              onPaste={handleOtpPaste}
              placeholder='000000'
              className={[
                'w-full px-3 py-3 text-center text-2xl tracking-[0.3em] font-mono',
                'border border-gray-300 bg-white text-gray-900 rounded-md',
                'focus:outline-none focus:ring-2 focus:ring-[#d4854a]',
                'focus:border-transparent placeholder:text-gray-400'
              ].join(' ')}
              required
              disabled={isSubmitting}
              autoComplete='one-time-code'
            />
            <p className='mt-1 text-xs text-gray-500 text-center'>
              Enter the 6-digit code from your invitation email
            </p>
          </div>

          <button
            type='submit'
            disabled={isSubmitting || !email || otpCode.length !== 6}
            className={[
              'w-full bg-[#d4854a] text-white py-2 px-4 rounded-md',
              'hover:bg-[#c17640] transition-colors font-medium',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'flex items-center justify-center gap-2'
            ].join(' ')}
          >
            {isSubmitting ? (
              <>
                <Loader2 className='h-4 w-4 animate-spin' />
                Verifying...
              </>
            ) : (
              'Verify & Continue'
            )}
          </button>
        </form>

        <div className='mt-6 pt-4 border-t border-gray-200'>
          <p className='text-sm text-gray-600 text-center mb-3'>
            Didn&apos;t receive a code or it expired?
          </p>
          <p className='text-xs text-gray-500 text-center'>
            Contact your administrator to send a new invite. Codes expire for your security.
          </p>
        </div>

        <div className='mt-4 text-center'>
          <p className='text-sm text-gray-600'>
            Already have an account?{' '}
            <a href='/auth' className='text-[#d4854a] hover:text-[#c17640] font-semibold'>
              Sign in
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};
